/**
 * The agency's money calendar: every client's paychecks and deal payments on
 * one timeline, with the agency's fee beside each (shared/moneyCalendar.ts).
 *
 * Reads what the athlete's own calendar reads — active playing contracts,
 * signed deals, recorded income, and the first regular-season game from the
 * calendar — for every client in the book at once, then runs the same rule
 * per client so the two screens can never disagree.
 *
 * Pure apart from the rows it is handed: the app reads them with the signed-in
 * agent's session (api/moneyCalendar.ts) and the Claude connector with the
 * same person's (api/_needsYou.ts), so both see one answer. Amounts here are the
 * agency's own records, so nothing is withheld; `amount_shared` only decides
 * what the athlete sees.
 */

import { buildMoneyCalendar, type MoneyItem, type MoneyGap } from './moneyCalendar.js'
import { findMissedMoney, type MissedMoney } from './missedMoney.js'
import { seasonOf } from './payModel.js'
import { playingContractFilter } from './standingRules.js'
import type { SalaryCrossCheck } from './salaryCrossCheck.js'
import type { RecordedCrossCheck } from './recordedCrossCheck.js'

export interface AgencyMoneyItem extends MoneyItem {
  client_id: string
  client_name: string
}

export interface AgencyMoneyGap extends MoneyGap {
  client_id: string
  client_name: string
}

export interface AgencyMissedMoney extends MissedMoney {
  client_id: string
  client_name: string
}

/** A contract whose figures a second source does not back up (the daily contractCrossCheck job). */
export interface FigureCheck {
  client_id: string
  client_name: string
  contract_id: string
  contract_title: string | null
  /** Null when the salary check has not run on this contract yet. */
  check: (SalaryCrossCheck & { checked_at?: string }) | null
  /** The recorded paychecks, when they do not add up. */
  recorded: (RecordedCrossCheck & { checked_at?: string }) | null
}

export interface AgencyMoneyCalendar {
  items: AgencyMoneyItem[]
  gaps: AgencyMoneyGap[]
  /** Expected, the date has gone, and no record answers for it. */
  missed: AgencyMissedMoney[]
  /**
   * Everything already due that nothing answers for, ready to be recorded as
   * arrived — including clients with no income on file, who are left out of
   * `missed` so they are not alarmed about money nobody was tracking.
   *
   * The timeline above deliberately never projects a paycheck before today,
   * so these cannot be filtered out of `items`: a check that came last Monday
   * is not in it.
   */
  due_to_record: AgencyMoneyItem[]
  /**
   * Clients whose paychecks cannot be watched because no income has ever
   * been recorded for them. Said out loud, because an empty missed list
   * would otherwise read as "everything arrived".
   */
  unwatched: { client_id: string; client_name: string }[]
  /** Known amounts expected in the next 30 days, and the agency's share of them. */
  next_30_days: number
  fee_next_30_days: number
  last_30_days: number
  today: string
  /**
   * Contracts whose pay or term ESPN does not back up, and how the rest came
   * out. Nothing is changed by the check; these are for a person to settle.
   */
  figure_checks: FigureCheck[]
  figure_checks_summary: { agree: number; unchecked: number; espn_has_nothing: number }
}

const DAY = 86_400_000
const dayString = (at: Date) => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' }).format(at)

/** Reads every row of a collection the caller's session may see. */
export type ListRows = <T = Record<string, any>>(collection: string, filter: string, fields: string, sort?: string) => Promise<T[]>

/**
 * @param list How rows are read: the app's session, or the connector's.
 * @param agency A ready filter on agency_id, escaped by the caller.
 * @param clientIds Only these clients (the page's work view), or null for the whole book.
 */
export async function loadAgencyMoneyCalendar(list: ListRows, agency: string, clientIds: string[] | null = null, daysAhead = 120): Promise<AgencyMoneyCalendar> {
  const all = list
  const today = dayString(new Date())
  const seasonStartFloor = dayString(new Date(Date.now() - 210 * DAY))
  // Wide enough for the missed-payment check below, which asks whether any
  // income has been recorded for this client in the last 90 days at all.
  const receivedFrom = dayString(new Date(Date.now() - 180 * DAY))

  const [clients, contracts, deals, income, games] = await Promise.all([
    all(`clients`, agency, 'id,full_name,first_name,last_name,roster_status'),
    all('contracts', `${agency} && (${playingContractFilter()}) && status = "active"`, 'id,client_id,title,yearly_breakdown,paycheck_weeks,paycheck_cadence,commission_rate,start_date,accrued_seasons,roster_history,cross_checks'),
    all('deals', `${agency} && stage = "Executed"`, 'id,client_id,title,payment_schedule,show_value_to_client,commission_rate'),
    all('transactions', `${agency} && type = "athlete_income" && transaction_date >= "${receivedFrom}"`, 'id,client_id,amount,transaction_date,description,deal_id,contract_id,status'),
    all('calendar_events', `${agency} && event_type = "game" && season_type = 2 && start_date >= "${seasonStartFloor}"`, 'id,client_ids,start_date', 'start_date'),
  ])

  const wanted = clientIds == null ? null : new Set(clientIds)
  const items: AgencyMoneyItem[] = []
  const gaps: AgencyMoneyGap[] = []
  const missed: AgencyMissedMoney[] = []
  const dueToRecord: AgencyMoneyItem[] = []
  const unwatched: { client_id: string; client_name: string }[] = []
  const figureChecks: FigureCheck[] = []
  const figureSummary = { agree: 0, unchecked: 0, espn_has_nothing: 0 }

  for (const client of clients) {
    if (wanted && !wanted.has(client.id)) continue
    const mine = <T extends { client_id?: string }>(rows: T[]) => rows.filter(r => r.client_id === client.id)
    const myContracts = mine(contracts as any[])
    const myDeals = mine(deals as any[])
    const myIncome = mine(income as any[])
    if (myContracts.length === 0 && myDeals.length === 0 && myIncome.length === 0) continue

    const firstGames: Record<number, string> = {}
    for (const g of games as any[]) {
      const on = Array.isArray(g.client_ids) ? g.client_ids : []
      if (!on.includes(client.id)) continue
      const day = dayString(new Date(String(g.start_date).replace(' ', 'T')))
      const season = seasonOf(day)
      if (!firstGames[season] || day < firstGames[season]) firstGames[season] = day
    }

    const calendarContracts = myContracts.map(c => ({
        id: c.id, title: c.title || null,
        yearly_breakdown: Array.isArray(c.yearly_breakdown) ? c.yearly_breakdown : null,
        paycheck_weeks: c.paycheck_weeks > 0 ? c.paycheck_weeks : null,
        paycheck_cadence: c.paycheck_cadence === 'weekly' || c.paycheck_cadence === 'biweekly' ? c.paycheck_cadence : null,
        commission_rate: c.commission_rate ?? null,
        accrued_seasons: c.accrued_seasons ?? null,
        roster_history: Array.isArray(c.roster_history) ? c.roster_history : null,
    }))
    const calendarDeals = myDeals.map(d => ({
        id: d.id, title: d.title || null,
        payment_schedule: Array.isArray(d.payment_schedule) ? d.payment_schedule : null,
        // The agency sees its own figures; this switch is the athlete's view.
        amount_shared: true,
        commission_rate: d.commission_rate ?? null,
    }))

    const calendar = buildMoneyCalendar({
      contracts: calendarContracts,
      firstGames,
      rosterStatus: client.roster_status || null,
      deals: calendarDeals,
      received: myIncome.map(t => ({ id: t.id, date: String(t.transaction_date ?? ''), amount: Number(t.amount), description: t.description || null, deal_id: t.deal_id || null, contract_id: t.contract_id || null, assumed: t.status === 'assumed' })),
      today,
      daysBack: 30,
      daysAhead,
    })

    const name = client.full_name || [client.first_name, client.last_name].filter(Boolean).join(' ')
    for (const c of myContracts as any[]) {
      const checks = c.cross_checks && typeof c.cross_checks === 'object' ? c.cross_checks : {}
      const check = checks.salary ?? null
      const recorded = checks.recorded?.verdict === 'differs' ? checks.recorded : null
      const salaryFine = check != null && (check.verdict === 'missing_espn' || (check.verdict === 'agrees' && check.term?.agrees !== false))
      if (!check) figureSummary.unchecked++
      else if (check.verdict === 'missing_espn') figureSummary.espn_has_nothing++
      else if (salaryFine) figureSummary.agree++
      if ((check == null || salaryFine) && !recorded) continue
      figureChecks.push({ client_id: client.id, client_name: name, contract_id: c.id, contract_title: c.title || null, check: salaryFine ? null : check, recorded })
    }
    for (const item of calendar.items) items.push({ ...item, client_id: client.id, client_name: name })
    for (const gap of calendar.gaps) gaps.push({ ...gap, client_id: client.id, client_name: name })

    // What was expected and never recorded. This needs the past projected,
    // which the timeline above deliberately does not do, so it is its own
    // pass over the same inputs — the same pure rule the nightly job runs.
    const receipts = myIncome.map(t => ({
      date: String(t.transaction_date ?? '').slice(0, 10),
      amount: Number(t.amount),
      contract_id: t.contract_id || null,
      deal_id: t.deal_id || null,
    }))
    const past = buildMoneyCalendar({
      contracts: calendarContracts,
      firstGames,
      rosterStatus: client.roster_status || null,
      deals: calendarDeals,
      received: [],
      today,
      daysBack: 120,
      daysAhead: 0,
      includePastPaychecks: true,
    })
    const notBefore: Record<string, string> = {}
    for (const c of myContracts as any[]) {
      const start = String(c.start_date ?? '').slice(0, 10)
      if (start) notBefore[c.id] = start
    }
    for (const m of findMissedMoney({ items: past.items, receipts, today, notBefore })) {
      missed.push({ ...m, client_id: client.id, client_name: name })
    }
    // The same rule, asked what could be recorded rather than what to worry
    // about. Each finding is matched back to the dated item it came from,
    // because that is what the record button needs.
    // No grace period either: the five days exist so nobody is alarmed about a
    // club that pays on the Friday, and have nothing to do with whether an
    // agent may record the check that landed this morning.
    for (const m of findMissedMoney({ items: past.items, receipts, today, notBefore, requireTracked: false, graceDays: 0 })) {
      const item = past.items.find(i => i.source_id === m.source_id && i.date === m.date && i.kind !== 'received')
      if (item) dueToRecord.push({ ...item, client_id: client.id, client_name: name })
    }
    if (myContracts.length > 0 && receipts.length === 0) unwatched.push({ client_id: client.id, client_name: name })
  }

  items.sort((a, b) => a.date.localeCompare(b.date) || a.client_name.localeCompare(b.client_name))
  const t = Date.parse(`${today}T00:00:00Z`)
  const within30 = (i: AgencyMoneyItem) => Date.parse(`${i.date}T00:00:00Z`) - t <= 30 * DAY
  const ahead = items.filter(i => i.status !== 'received' && within30(i))
  missed.sort((a, b) => a.date.localeCompare(b.date) || a.client_name.localeCompare(b.client_name))
  dueToRecord.sort((a, b) => a.date.localeCompare(b.date) || a.client_name.localeCompare(b.client_name))
  return {
    items,
    gaps,
    missed,
    due_to_record: dueToRecord,
    unwatched,
    next_30_days: ahead.reduce((s, i) => s + (i.amount ?? 0), 0),
    fee_next_30_days: ahead.reduce((s, i) => s + (i.fee ?? 0), 0),
    last_30_days: items.filter(i => i.status === 'received' && Date.parse(`${i.date}T00:00:00Z`) <= t).reduce((s, i) => s + (i.amount ?? 0), 0),
    today,
    figure_checks: figureChecks.sort((a, b) => a.client_name.localeCompare(b.client_name)),
    figure_checks_summary: figureSummary,
  }
}
